import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Building2, Home, CalendarDays, ChevronRight, ChevronLeft } from 'lucide-react';
import { patientApi } from '../api';

const STEPS = ['Select Tests', 'Collection Type', 'Date & Slot', 'Payment & Review', 'Confirm'];

export default function PatientLabBooking() {
  const { labProfileId } = useParams<{ labProfileId: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [tests, setTests] = useState<any[]>([]);
  const [packages, setPackages] = useState<any[]>([]);
  const [slots, setSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Selections
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [selectedPackages, setSelectedPackages] = useState<string[]>([]);
  const [collectionType, setCollectionType] = useState<'LAB_VISIT' | 'HOME_COLLECTION'>('LAB_VISIT');
  const [slotDate, setSlotDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedSlot, setSelectedSlot] = useState('');
  const [prescriptionId, setPrescriptionId] = useState('');
  const [referringDoctor, setReferringDoctor] = useState('');
  // Payment is collected at the lab — there is no online gateway yet, so no
  // simulated "Pay Now" step exists and the booking is never gated on one.

  useEffect(() => {
    if (!labProfileId) return;
    let alive = true;
    Promise.all([
      patientApi.lab.getLabTests(labProfileId).then((d: any) => { if (alive) setTests(Array.isArray(d) ? d : d?.tests || []); }),
      patientApi.lab.getLabPackages(labProfileId).then((d: any) => { if (alive) setPackages(Array.isArray(d) ? d : d?.packages || []); }),
    ]).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [labProfileId]);

  useEffect(() => {
    let alive = true;
    if (step === 2 && labProfileId) {
      patientApi.lab.getLabSlots(labProfileId, { date: slotDate })
        .then((d: any) => { if (alive) setSlots(Array.isArray(d) ? d : d?.slots || []); })
        .catch(() => { if (alive) setSlots([]); });
    }
    return () => { alive = false; };
  }, [step, slotDate, labProfileId]);

  const toggleTest = (id: string) => setSelectedTests(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const togglePkg = (id: string) => setSelectedPackages(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const selectedTestItems = tests.filter(t => selectedTests.includes(t.id || t._id));
  const selectedPkgItems = packages.filter(p => selectedPackages.includes(p.id || p._id));
  const total = selectedTestItems.reduce((s, t) => s + (t.price || 0), 0) + selectedPkgItems.reduce((s, p) => s + (p.price || p.discountedPrice || 0), 0);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await patientApi.lab.bookAppointment({
        labProfileId,
        testIds: selectedTests,
        packageIds: selectedPackages,
        collectionType,
        slotDate,
        slotId: selectedSlot,
        prescriptionId: prescriptionId || undefined,
        referringDoctorName: referringDoctor || undefined,
      });
      navigate('/patient/lab-appointments');
    } catch {
      alert('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-bold text-gray-900">Book Lab Appointment</h1>
        <p className="text-sm text-gray-500 mt-1">Lab services on NeoHealthCard</p>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 flex-1">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
              i < step ? 'bg-teal-600 text-white' : i === step ? 'bg-teal-100 text-teal-700 ring-2 ring-teal-300' : 'bg-gray-200 text-gray-400'
            }`}>
              {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span className="text-xs text-gray-500 hidden sm:block truncate">{s}</span>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-gray-200 hidden sm:block" />}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Select Tests & Packages</h2>
            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />)}</div>
            ) : (
              <>
                {tests.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-gray-400 uppercase">Tests</p>
                    {tests.map((t: any) => {
                      const id = t.id || t._id;
                      return (
                        <label key={id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={selectedTests.includes(id)} onChange={() => toggleTest(id)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                          <span className="flex-1 text-sm text-gray-800">{t.testName || t.name}</span>
                          <span className="text-sm font-semibold text-teal-700">₹{Number(t.price || 0).toLocaleString('en-IN')}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                {packages.length > 0 && (
                  <div className="space-y-1 mt-4">
                    <p className="text-xs font-medium text-gray-400 uppercase">Packages</p>
                    {packages.map((p: any) => {
                      const id = p.id || p._id;
                      return (
                        <label key={id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                          <input type="checkbox" checked={selectedPackages.includes(id)} onChange={() => togglePkg(id)} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
                          <span className="flex-1 text-sm text-gray-800">{p.packageName || p.name}</span>
                          <span className="text-sm font-semibold text-teal-700">₹{Number(p.price || p.discountedPrice || 0).toLocaleString('en-IN')}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                  <span className="text-sm text-gray-500">{selectedTests.length + selectedPackages.length} selected</span>
                  <span className="text-base font-bold text-teal-700">Total: ₹{total.toLocaleString('en-IN')}</span>
                </div>
              </>
            )}
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Collection Type</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { value: 'LAB_VISIT' as const, icon: Building2, label: 'Lab Visit', desc: 'Visit the lab in person' },
                { value: 'HOME_COLLECTION' as const, icon: Home, label: 'Home Collection', desc: 'Collector comes to you' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setCollectionType(opt.value)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    collectionType === opt.value ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <opt.icon className={`w-6 h-6 mb-2 ${collectionType === opt.value ? 'text-teal-600' : 'text-gray-400'}`} />
                  <p className="text-sm font-semibold text-gray-900">{opt.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
            <div className="space-y-3 pt-3 border-t border-gray-100">
              <div>
                <label className="text-xs font-medium text-gray-500">Prescription ID (optional)</label>
                <input value={prescriptionId} onChange={e => setPrescriptionId(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="e.g. RX-12345" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500">Referring Doctor (optional)</label>
                <input value={referringDoctor} onChange={e => setReferringDoctor(e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" placeholder="Dr. Name" />
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Pick Date & Slot</h2>
            <input
              type="date"
              value={slotDate}
              onChange={e => setSlotDate(e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            {slots.length === 0 ? (
              <p className="text-sm text-gray-400 py-4 text-center">No slots available for this date</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {slots.map((slot: any) => {
                  const id = slot.id || slot._id || slot.time;
                  const available = slot.available !== false && slot.status !== 'FULL';
                  return (
                    <button
                      key={id}
                      disabled={!available}
                      onClick={() => setSelectedSlot(id)}
                      className={`py-2.5 px-2 rounded-lg text-xs font-medium border transition-colors ${
                        selectedSlot === id
                          ? 'bg-teal-600 border-teal-600 text-white'
                          : available
                            ? 'bg-green-50 border-green-200 text-green-700 hover:border-green-400'
                            : 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {slot.time || slot.startTime || slot.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Payment & Review</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Collection</span><span className="font-medium text-gray-900">{collectionType === 'HOME_COLLECTION' ? 'Home Collection' : 'Lab Visit'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-medium text-gray-900">{slotDate}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Slot</span><span className="font-medium text-gray-900">{selectedSlot || '—'}</span></div>
              {prescriptionId && <div className="flex justify-between"><span className="text-gray-500">Prescription</span><span className="font-medium text-gray-900">{prescriptionId}</span></div>}
              {referringDoctor && <div className="flex justify-between"><span className="text-gray-500">Referring Doctor</span><span className="font-medium text-gray-900">{referringDoctor}</span></div>}
              <hr className="border-gray-100" />
              {selectedTestItems.map(t => (
                <div key={t.id || t._id} className="flex justify-between">
                  <span className="text-gray-700">{t.testName || t.name}</span>
                  <span className="text-gray-900 font-medium">₹{Number(t.price || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
              {selectedPkgItems.map(p => (
                <div key={p.id || p._id} className="flex justify-between">
                  <span className="text-gray-700">{p.packageName || p.name}</span>
                  <span className="text-gray-900 font-medium">₹{Number(p.price || p.discountedPrice || 0).toLocaleString('en-IN')}</span>
                </div>
              ))}
              <hr className="border-gray-100" />
              <div className="flex justify-between text-base font-bold">
                <span className="text-gray-900">Total</span>
                <span className="text-teal-700">₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            {/* Payment Mode */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-3">Payment Mode</p>
              <div className="p-4 rounded-xl border-2 border-purple-500 bg-purple-50 text-left">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
                  <span className="text-sm font-semibold text-gray-900">Pay at Lab</span>
                </div>
                <p className="text-xs text-gray-500">Pay when you visit the lab · Online payment gateway coming soon</p>
              </div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4">
            <h2 className="text-sm font-semibold text-gray-900">Confirm Booking</h2>

            {/* Booking Summary */}
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Tests</span><span className="font-medium text-gray-900">{selectedTests.length + selectedPackages.length} selected</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Collection</span><span className="font-medium text-gray-900">{collectionType === 'HOME_COLLECTION' ? 'Home Collection' : 'Lab Visit'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Date</span><span className="font-medium text-gray-900">{slotDate}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="font-bold text-teal-700">₹{total.toLocaleString('en-IN')}</span></div>
              <hr className="border-gray-200" />
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Payment</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
                  Pay at Lab
                </span>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-800">Pay at Lab</p>
                <p className="text-xs text-purple-600">₹{total.toLocaleString('en-IN')} to be paid at the lab during your visit</p>
              </div>
            </div>

            <p className="text-[10px] text-center text-gray-400 leading-relaxed">
              NeoHealthCard™ · Online payment gateway coming soon · Every transaction is audit logged
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          onClick={() => setStep(s => s - 1)}
          disabled={step === 0}
          className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        {step < 4 ? (
          <button
            onClick={() => setStep(s => s + 1)}
            className="flex items-center gap-1 px-5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
          >
            {submitting ? 'Booking...' : 'Confirm Booking'}
          </button>
        )}
      </div>
    </div>
  );
}
